import { ApiError, isApiConfigured, sendTelegram, submitSurvey } from '@/services/api';
import {
  db,
  enqueueSync,
  getSurvey,
  loadIntervals,
  pendingSyncItems,
  updateSurvey,
  updateSyncItem,
} from '@/db/localDatabase';
import type { SyncQueueItem } from '@/types/survey';

/**
 * Background delivery of completed surveys.
 *
 * The queue is persistent (IndexedDB), so a submission survives a refresh, a
 * crash, or the phone being switched off overnight. Nothing is ever deleted
 * locally because of a sync result — success only flips flags.
 */

/** 30s, 1m, 2m, 4m, 8m, 15m, 30m, then 1h forever. */
const BACKOFF_SCHEDULE_MS = [
  30_000,
  60_000,
  2 * 60_000,
  4 * 60_000,
  8 * 60_000,
  15 * 60_000,
  30 * 60_000,
];
const MAX_BACKOFF_MS = 60 * 60_000;

/** Attempts after which we stop calling it "retrying" in the UI. */
export const ATTEMPTS_BEFORE_WARNING = 5;

export function backoffFor(attempts: number): number {
  const index = Math.max(0, attempts - 1);
  const base = BACKOFF_SCHEDULE_MS[index] ?? MAX_BACKOFF_MS;
  // Jitter (+/-20%) so ten surveyors reconnecting at once do not hit the Worker
  // in the same millisecond.
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(5_000, Math.round(base + jitter));
}

export type SyncListener = (state: SyncState) => void;

export interface SyncState {
  running: boolean;
  pending: number;
  lastRunAt?: number;
  lastError?: string;
}

let state: SyncState = { running: false, pending: 0 };
const listeners = new Set<SyncListener>();

function emit(changes: Partial<SyncState>): void {
  state = { ...state, ...changes };
  for (const listener of listeners) listener(state);
}

export function subscribeSync(listener: SyncListener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export function getSyncState(): SyncState {
  return state;
}

export async function refreshPendingCount(): Promise<number> {
  const pending = await db.syncQueue.filter((item) => !item.completed).count();
  emit({ pending });
  return pending;
}

/** Queues both delivery channels for a survey that the user just submitted. */
export async function queueSubmission(surveyId: string): Promise<void> {
  await enqueueSync(surveyId, 'saveSurvey');
  await enqueueSync(surveyId, 'sendTelegram');
  await refreshPendingCount();
}

async function runItem(item: SyncQueueItem): Promise<void> {
  const survey = await getSurvey(item.surveyId);
  if (!survey) {
    // The survey is gone; nothing left to deliver.
    await updateSyncItem(item.id, { completed: true, lastError: 'Survey no longer on device.' });
    return;
  }

  // Telegram needs the D1 row to exist first, so that the message and the
  // database never disagree about what was saved.
  if (item.action === 'sendTelegram' && !survey.databaseSynced) {
    await updateSyncItem(item.id, { nextRetryAt: Date.now() + 5_000 });
    return;
  }

  const attempts = item.attempts + 1;
  await updateSyncItem(item.id, { attempts, lastAttemptAt: Date.now() });

  try {
    // The 15 minute breakdown is rebuilt from the event log at send time, so a
    // retry always carries the same numbers the summary screen shows.
    const intervals = await loadIntervals(survey.id);

    if (item.action === 'saveSurvey') {
      await submitSurvey(survey, intervals);
      await updateSurvey(survey.id, {
        databaseSynced: true,
        syncedAt: Date.now(),
        status: survey.telegramSent ? 'synced' : 'pending_sync',
      });
    } else {
      await sendTelegram(survey, intervals);
      const fresh = await getSurvey(survey.id);
      await updateSurvey(survey.id, {
        telegramSent: true,
        status: fresh?.databaseSynced ? 'synced' : 'pending_sync',
      });
    }
    await updateSyncItem(item.id, { completed: true, lastError: undefined });
  } catch (error) {
    const apiError =
      error instanceof ApiError
        ? error
        : new ApiError('server', error instanceof Error ? error.message : 'Unknown error');

    if (!apiError.retryable) {
      // The server will never accept this payload. Stop hammering it, keep the
      // data locally, and surface the reason.
      await updateSyncItem(item.id, {
        completed: true,
        lastError: `${apiError.message} (not retried automatically)`,
      });
    } else {
      await updateSyncItem(item.id, {
        nextRetryAt: Date.now() + backoffFor(attempts),
        lastError: apiError.message,
      });
    }
    throw apiError;
  }
}

/**
 * Processes every queue item that is due. Safe to call often — it is a no-op
 * when offline, unconfigured, or already running.
 */
export async function processQueue(options: { force?: boolean } = {}): Promise<SyncState> {
  if (state.running) return state;
  if (!isApiConfigured()) {
    emit({ lastError: 'Server address is not configured in this build.' });
    return state;
  }
  /*
   * `navigator.onLine` is only a hint, and on iOS (especially an installed PWA
   * resumed from the background) it can report false while the network is
   * perfectly usable. Skipping on it stranded submissions until the surveyor
   * pressed Retry Sync by hand. We now attempt the request anyway: if the device
   * really is offline the fetch fails fast and the item simply stays queued.
   */
  if (typeof navigator !== 'undefined' && navigator.onLine === false && !options.force) {
    // Still attempt, but only when something is actually waiting.
    const waiting = await refreshPendingCount();
    if (waiting === 0) return state;
  }

  emit({ running: true, lastError: undefined });
  let lastError: string | undefined;
  try {
    // `force` (the manual Retry Sync button) ignores the backoff clock.
    const due = options.force
      ? (await db.syncQueue.filter((item) => !item.completed).toArray()).sort(
          (a, b) => a.createdAt - b.createdAt,
        )
      : await pendingSyncItems();

    for (const item of due) {
      try {
        await runItem(item);
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Sync failed.';
      }
    }
  } finally {
    await refreshPendingCount();
    emit({ running: false, lastRunAt: Date.now(), lastError });
  }
  return state;
}

let intervalId: ReturnType<typeof setInterval> | undefined;
let started = false;

/**
 * Starts automatic synchronisation: on reconnect, when the app is brought back
 * to the foreground, and on a slow timer as a safety net. Deliberately NOT a
 * per-second poll.
 */
export function startAutoSync(intervalMs = 60_000): () => void {
  if (started) return () => undefined;
  started = true;

  const run = () => {
    void processQueue();
  };

  const onOnline = () => {
    void processQueue({ force: true });
  };

  /*
   * iOS freezes timers in a backgrounded tab or an installed PWA, so the
   * interval below cannot be relied on there. Every route back into the
   * foreground therefore triggers a forced pass: visibility change, window
   * focus, and pageshow (which also fires when Safari restores a page from its
   * back/forward cache, where no other event would).
   */
  const onResume = () => {
    if (document.visibilityState === 'visible') void processQueue({ force: true });
  };
  const onPageShow = () => {
    void processQueue({ force: true });
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('focus', onResume);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onResume);
  intervalId = setInterval(run, intervalMs);

  void refreshPendingCount().then(run);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('focus', onResume);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onResume);
    if (intervalId) clearInterval(intervalId);
    started = false;
  };
}
